// Shared parsing + types for the OAS Reconciliation dashboard (Mass Balance enhancement).
// Three source layouts, all read client-side via SheetJS:
//   - OAS Material Download  -> InvRow[]   (daily tank inventory; density + temperature)
//   - OAS Ext Movement       -> MovRow[]   (external receipts/deliveries; volume-only)
//   - Daily Yields           -> YieldData  (Movements m3 per-grade balance + MTD loss summary)
// See MASS_BALANCE_ENHANCEMENT_PLAN.md.
import * as XLSX from 'xlsx';

export interface InvRow { dateKey: string; tank: string; cgid: string; sap: string; desc: string; density: number | null; temp: number | null; vol: number; mass: number; }
export interface MovRow { dateKey: string; cgid: string; desc: string; stype: string; ind: 'R' | 'D' | ''; vol: number; mass: number; density: number | null; temp: number | null; }
export interface YieldRow { sap: string; desc: string; opening: number; receipts: number; deliveries: number; ownCons: number; closing: number; intakeProd: number; adjustments: number; netProd: number; group: string; }
export interface MtdSummary { crudeProcessed: number | null; totalFuel: number | null; unaccountedLoss: number | null; lossPct: number | null; fuelPct: number | null; }
// basis: which physical quantity the per-grade balance is in. EET publishes the same balance twice:
//   "Daily Yields <Month>.xlsx"  -> sheet "Movements m3"  -> volume (m3)
//   "1-31 <Mon>-26 DAILY OAS MB.xlsx" -> sheet "Movements" (+ a Summary in MT) -> mass (MT)
// Both parse identically; only the unit differs, so the caller MUST honour `basis`.
export type YieldBasis = 'volume' | 'mass';
export interface YieldData { rows: YieldRow[]; mtd: MtdSummary; basis: YieldBasis; sheet: string; }

export const num = (v: any): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
export const numOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};
export const toDateKey = (v: any): string => {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'number') {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const colIndex = (header: any[]): Record<string, number> => {
  const m: Record<string, number> = {};
  header.forEach((h, i) => { if (h !== null && h !== undefined && h !== '') m[String(h).trim().toUpperCase()] = i; });
  return m;
};
const rowsOf = (ws: XLSX.WorkSheet): any[][] => XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, blankrows: false });

export const parseMaterial = (wb: XLSX.WorkBook): InvRow[] | null => {
  for (const sn of wb.SheetNames) {
    const rows = rowsOf(wb.Sheets[sn]); if (!rows.length) continue;
    const c = colIndex(rows[0]);
    if (!('STOCK_DATE' in c) || !('NETT_VOL_M3_15C' in c)) continue;
    const out: InvRow[] = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i]; if (!r) continue;
      const cgid = String(r[c['COMMERCE_GRADE_ID'] ?? c['GRADE']] ?? '').trim();
      if (!cgid) continue;
      out.push({
        dateKey: toDateKey(r[c['STOCK_DATE']]),
        tank: String(r[c['TANK']] ?? '').trim(),
        cgid,
        sap: String(r[c['GRADE']] ?? '').trim(),   // SAP grade code — the reliable join key to Daily Yields
        desc: String(r[c['GRADE_DESCRIPTION'] ?? c['GRADE_DESC']] ?? '').trim(),
        density: numOrNull(r[c['DENSITY']]),
        temp: numOrNull(r[c['TEMPERATURE']]),
        vol: num(r[c['NETT_VOL_M3_15C']]),
        mass: num(r[c['TONNES']]),
      });
    }
    return out;
  }
  return null;
};

export const parseMovement = (wb: XLSX.WorkBook): MovRow[] | null => {
  for (const sn of wb.SheetNames) {
    const rows = rowsOf(wb.Sheets[sn]); if (!rows.length) continue;
    const c = colIndex(rows[0]);
    if (!('RECEIPT_DELIVER_IND' in c) || !('PARCEL_ACT_QTY_KL_15' in c)) continue;
    const out: MovRow[] = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i]; if (!r) continue;
      const cgid = String(r[c['COMMERCE_GRADE_ID'] ?? c['GRADE_ID']] ?? '').trim();
      if (!cgid) continue;
      const ind = String(r[c['RECEIPT_DELIVER_IND']] ?? '').trim().toUpperCase();
      const vol = Math.abs(num(r[c['PARCEL_ACT_QTY_KL_15']]));
      const mass = Math.abs(num(r[c['PARCEL_ACT_QTY_WEIGHT']]));
      // density not in the file; derive implied ρ = mass / volume when both present
      const density = vol > 1e-6 && mass ? mass / vol : numOrNull(r[c['PARCEL_DENSITY']]);
      out.push({
        dateKey: toDateKey(r[c['NOM_ACCOUNTING_DATE']]),
        cgid,
        desc: String(r[c['GRADE_DESC'] ?? c['GRADE_DESCRIPTION']] ?? '').trim(),
        stype: String(r[c['SHIPMENT_TYPE']] ?? '').trim().toUpperCase(),
        ind: ind === 'R' ? 'R' : ind === 'D' ? 'D' : '',
        vol, mass, density,
        temp: numOrNull(r[c['PARCEL_TEMPERATURE']]),
      });
    }
    return out;
  }
  return null;
};

// Which unit a Daily-Yields-style workbook is in. Sheet names are the primary signal ("Movements m3" /
// "Data dump m3" = volume); the condensed "DAILY OAS MB" variant has bare sheet names and a Summary
// sheet whose headers say MT. Default is volume (the historical assumption).
export const yieldsBasis = (wb: XLSX.WorkBook, sheet: string): YieldBasis => {
  if (/m3|m³|vol/i.test(sheet)) return 'volume';
  if (wb.SheetNames.some((n) => /m3|m³/i.test(n))) return 'volume';   // sibling tabs say m3
  const summary = wb.SheetNames.find((n) => n.trim().toLowerCase() === 'summary');
  if (summary) {
    const rr = rowsOf(wb.Sheets[summary]).slice(0, 6);
    if (rr.some((r) => (r || []).some((c: any) => typeof c === 'string' && /^\s*MT\s*$|\bin MT\b|tonnes?/i.test(c)))) return 'mass';
  }
  if (/^movements$|^data dump$/i.test(sheet.trim())) return 'mass';     // bare-named tabs = the MT variant
  return 'volume';
};

// EET's CONDENSED OAS Mass Balance export (first seen for September 2026): ONE sheet named exactly
// "MT" or "M3", a title banner row, then the header. It replaces "1-31 <Mon>-26 DAILY OAS MB.xlsx",
// and it is NOT a rename -- the layout differs:
//   * no GROUP column        -> category has to come from the m3 report, joined on SAP
//   * no ADJUSTMENTS column  -> the gross->net reconciliation is simply absent (treated as 0)
//   * consumption is SPLIT into CHARGE (feed) and FUEL (own use), where the older "Movements" sheet
//     carried one signed INTAKE(-) & PROD (+)
// VERIFIED against the September file, both bases, all 91 rows, worst residual 0.0:
//     Closing = Opening + Receipts - Deliveries - Charge + Production - Fuel
// so the signed quantity the rest of the pipeline expects is  intakeProd = Production - Charge - Fuel.
// CHARGE and FUEL never both appear on the same row.
// The sheet-name test is deliberately EXACT: the "Tonnes vs m3" study workbook has sheets called
// "OAS MT"/"OAS M3" and must not be mistaken for a balance.
const parseOasCondensed = (wb: XLSX.WorkBook): YieldData | null => {
  const sn = wb.SheetNames.find((n) => /^(mt|m3)$/i.test(n.trim()));
  if (!sn) return null;
  const rr = rowsOf(wb.Sheets[sn]);
  if (rr.length < 3) return null;
  let hi = -1;
  for (let i = 0; i < Math.min(6, rr.length); i++) {
    const h = colIndex(rr[i]);
    if ('OPENING' in h && 'CLOSING' in h && ('CHARGE' in h || 'PRODUCTION' in h)) { hi = i; break; }
  }
  if (hi < 0) return null;
  const c = colIndex(rr[hi]);
  const at = (r: any[], k: string) => (k in c ? num(r[c[k]]) : 0);
  const out: YieldRow[] = [];
  for (let i = hi + 1; i < rr.length; i++) {
    const r = rr[i];
    if (!r) continue;
    const sap = String(r[0] ?? '').trim();
    if (!/^\d{6,}$/.test(sap)) continue;        // skips blanks, subtotals and footers
    const charge = at(r, 'CHARGE'), prod = at(r, 'PRODUCTION'), fuel = at(r, 'FUEL');
    const row: YieldRow = {
      sap,
      desc: String(r[1] ?? '').trim(),
      opening: at(r, 'OPENING'),
      receipts: at(r, 'RECEIPTS'),
      deliveries: at(r, 'DELIVERIES'),
      ownCons: fuel,
      closing: at(r, 'CLOSING'),
      intakeProd: prod - charge - fuel,
      adjustments: 0,
      netProd: prod - charge - fuel,
      group: '',                                  // supplied from the m3 report by SAP (buildOasBalances)
    };
    if ([row.opening, row.receipts, row.deliveries, row.ownCons, row.closing, row.intakeProd]
      .some((v) => Math.abs(v) > 1e-6)) out.push(row);
  }
  if (!out.length) return null;
  return {
    rows: out,
    mtd: { crudeProcessed: null, totalFuel: null, unaccountedLoss: null, lossPct: null, fuelPct: null },
    basis: /^mt$/i.test(sn.trim()) ? 'mass' : 'volume',
    sheet: sn,
  };
};

// Daily Yields: the customer's own per-grade balance ("Movements m3" tab) + MTD loss summary.
export const parseDailyYields = (wb: XLSX.WorkBook): YieldData | null => {
  let rows: YieldRow[] | null = null;
  let sheet = '';
  for (const sn of wb.SheetNames) {
    const rr = rowsOf(wb.Sheets[sn]); if (!rr.length) continue;
    const c = colIndex(rr[0]);
    if (!('GRADE' in c) || !('INTAKE(-) & PROD (+)' in c)) continue;
    sheet = sn;
    const out: YieldRow[] = [];
    for (let i = 1; i < rr.length; i++) {
      const r = rr[i]; if (!r) continue;
      const sap = String(r[c['GRADE']] ?? '').trim();
      if (!sap) continue;
      const row: YieldRow = {
        sap,
        desc: String(r[c['GRADE DESCRIPTION'] ?? c['GRADE_DESCRIPTION']] ?? '').trim(),
        opening: num(r[c['OPENING']]),
        receipts: num(r[c['RECEIPTS']]),
        deliveries: num(r[c['DELIVERIES']]),
        ownCons: num(r[c['OWN CONSUMPTION']]),
        closing: num(r[c['CLOSING']]),
        intakeProd: num(r[c['INTAKE(-) & PROD (+)']]),
        adjustments: num(r[c['ADJUSTMENTS']]),
        netProd: num(r[c['NET PRODUCTION']]),
        group: String(r[c['GROUP']] ?? 'Other').trim() || 'Other',
      };
      // keep only grades with any activity
      if ([row.opening, row.receipts, row.deliveries, row.ownCons, row.closing, row.intakeProd, row.adjustments].some((v) => Math.abs(v) > 1e-6)) out.push(row);
    }
    rows = out; break;
  }
  // Not the classic "Movements" layout -> try the condensed MT/M3 export (September 2026 onwards).
  if (!rows) return parseOasCondensed(wb);

  // MTD loss summary — scan the "MTD" sheet's name/qty block (any two adjacent columns
  // where one holds the known labels and the next holds the number).
  const mtd: MtdSummary = { crudeProcessed: null, totalFuel: null, unaccountedLoss: null, lossPct: null, fuelPct: null };
  const mtdWs = wb.Sheets['MTD'];
  if (mtdWs) {
    const rr = rowsOf(mtdWs);
    for (const r of rr) {
      for (let j = 0; j < r.length - 1; j++) {
        const label = String(r[j] ?? '').trim().toLowerCase();
        const v = numOrNull(r[j + 1]);
        if (v === null) continue;
        if (label === 'crude+hsgo processed') mtd.crudeProcessed = v;
        else if (label === 'total fuel') mtd.totalFuel = v;
        else if (label === 'unaccounted loss') mtd.unaccountedLoss = v;
        else if (label === 'loss%') mtd.lossPct = v;
        else if (label === 'fuel %') mtd.fuelPct = v;
      }
    }
  }
  return { rows, mtd, basis: yieldsBasis(wb, sheet), sheet };
};

// ---- reference densities (kg/L @15C), curated seed for the Phase-1 density check ----
// Ordered specific -> generic; first keyword match wins.
export interface DensBand { kw: string; lo: number; hi: number; }
export const REF_DENSITY: DensBand[] = [
  { kw: 'iso butane', lo: 0.55, hi: 0.58 }, { kw: 'isobutane', lo: 0.55, hi: 0.58 },
  { kw: 'propylene', lo: 0.51, hi: 0.54 }, { kw: 'propane', lo: 0.50, hi: 0.53 },
  { kw: 'butane', lo: 0.57, hi: 0.60 },
  { kw: 'alkylate', lo: 0.68, hi: 0.72 }, { kw: 'reformate', lo: 0.79, hi: 0.83 },
  { kw: 'platformate', lo: 0.79, hi: 0.83 }, { kw: 'raffinate', lo: 0.66, hi: 0.74 },
  { kw: 'ethanol', lo: 0.78, hi: 0.80 }, { kw: 'toluene', lo: 0.86, hi: 0.88 },
  { kw: 'benzene', lo: 0.87, hi: 0.89 }, { kw: 'ethyl benzene', lo: 0.86, hi: 0.88 },
  { kw: 'sulphur', lo: 1.75, hi: 1.90 }, { kw: 'bitumen', lo: 1.00, hi: 1.06 },
  { kw: 'slurry', lo: 1.02, hi: 1.10 }, { kw: 'fuel oil', lo: 0.94, hi: 1.01 },
  { kw: 'long res', lo: 0.94, hi: 1.02 }, { kw: 'residue', lo: 0.94, hi: 1.02 },
  { kw: 'vgo', lo: 0.90, hi: 0.95 }, { kw: 'cycle oil', lo: 0.90, hi: 0.98 },
  { kw: 'jet', lo: 0.79, hi: 0.82 }, { kw: 'kero', lo: 0.79, hi: 0.82 }, { kw: 'avtur', lo: 0.79, hi: 0.82 },
  { kw: 'diesel', lo: 0.82, hi: 0.86 }, { kw: 'gas oil', lo: 0.82, hi: 0.90 }, { kw: 'gasoil', lo: 0.82, hi: 0.90 },
  { kw: 'naphtha', lo: 0.65, hi: 0.74 }, { kw: 'gasoline', lo: 0.72, hi: 0.78 }, { kw: 'mogas', lo: 0.72, hi: 0.78 },
  { kw: 'crude', lo: 0.80, hi: 0.92 }, { kw: 'blend', lo: 0.80, hi: 0.92 },
];
export const expectedDensity = (desc: string): DensBand | null => {
  const d = String(desc).toLowerCase();
  for (const b of REF_DENSITY) if (d.includes(b.kw)) return b;
  return null;
};

// read an OAS/Yields workbook and route to the right parser; returns what it found
export type ParsedKind = 'material' | 'movement' | 'yields' | null;
export const classify = (wb: XLSX.WorkBook): { kind: ParsedKind; inv?: InvRow[]; mov?: MovRow[]; yields?: YieldData } => {
  const mat = parseMaterial(wb); if (mat && mat.length) return { kind: 'material', inv: mat };
  const mv = parseMovement(wb); if (mv && mv.length) return { kind: 'movement', mov: mv };
  const yd = parseDailyYields(wb); if (yd && yd.rows.length) return { kind: 'yields', yields: yd };
  return { kind: null };
};

// Period ("YYYY-MM") of a Daily Yields workbook. The yield rows themselves carry no dates, so read the
// daily-date header the workbook's Summary sheet has (row 3: one Date cell per day of the month) and
// take the modal month. Falls back to scanning the first few rows of every sheet. Returns null when
// nothing date-like is found — callers then try the filename before giving up.
export const yieldsPeriodKey = (wb: XLSX.WorkBook): string | null => {
  const counts: Record<string, number> = {};
  const scan = (sheetName: string, maxRows: number) => {
    const ws = wb.Sheets[sheetName]; if (!ws) return;
    const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, blankrows: false, range: 0 }).slice(0, maxRows);
    for (const r of rows) for (const c of r) {
      if (!(c instanceof Date)) continue;
      const m = toDateKey(c).slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(m)) counts[m] = (counts[m] || 0) + 1;
    }
  };
  scan('Summary', 6);
  if (!Object.keys(counts).length) for (const n of wb.SheetNames) scan(n, 6);
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : null;
};

// Period from a filename like "Daily Yields August 2026.xlsx" / "OAS Ext Movement July-26.xls" / "2026-08 ...".
export const periodFromFilename = (name: string): string | null => {
  const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const monthIdx = (s: string) => MONTHS.findIndex((m) => m.startsWith(s.toLowerCase().slice(0, 3)));
  let m = name.match(/([A-Za-z]{3,})[\s-]+(20\d{2})\b/);            // "August 2026", "July-2026"
  if (m && monthIdx(m[1]) >= 0) return `${m[2]}-${String(monthIdx(m[1]) + 1).padStart(2, '0')}`;
  m = name.match(/([A-Za-z]{3,})[\s-]+(\d{2})\b/);                   // "July-26", "June 26"
  if (m && monthIdx(m[1]) >= 0) return `20${m[2]}-${String(monthIdx(m[1]) + 1).padStart(2, '0')}`;
  m = name.match(/(20\d{2})[-_ ]?(0[1-9]|1[0-2])\b/);                 // "2026-08"
  if (m) return `${m[1]}-${m[2]}`;
  return null;
};
